/**
 * StepMore — optional step for languages and hobbies.
 * Fully skippable. User can click "Passer →" in the wizard footer.
 */

import { X, Plus } from 'lucide-react';
import { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import type { CVData } from '../../../types';
import type { Dispatch, SetStateAction } from 'react';

interface StepMoreProps {
  data: CVData;
  setData: Dispatch<SetStateAction<CVData>>;
  removeLanguage: (index: number) => void;
  removeHobby: (index: number) => void;
}

export function StepMore({ data, setData, removeLanguage, removeHobby }: StepMoreProps) {
  const [newLang, setNewLang] = useState('');
  const [newHobby, setNewHobby] = useState('');

  const handleAddLang = () => {
    const trimmed = newLang.trim();
    if (!trimmed) return;
    setData((prev) => ({ ...prev, languages: [...prev.languages, trimmed] }));
    setNewLang('');
  };

  const handleAddHobby = () => {
    const trimmed = newHobby.trim();
    if (!trimmed) return;
    setData((prev) => ({ ...prev, hobbies: [...prev.hobbies, trimmed] }));
    setNewHobby('');
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Langues & centres d'intérêt</h1>
        <p className="text-sm text-white/60">
          Optionnel — tu peux passer cette étape et revenir plus tard.
        </p>
      </div>

      {/* Languages */}
      <div className="space-y-3">
        <Label className="text-sm text-white/80">Langues</Label>
        <div className="flex flex-wrap gap-2">
          {data.languages.map((lang, i) => (
            <span
              key={i}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-sm text-blue-300"
            >
              {lang}
              <button
                type="button"
                onClick={() => removeLanguage(i)}
                className="text-blue-400/50 hover:text-blue-300 transition-colors"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            value={newLang}
            onChange={(e) => setNewLang(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLang(); } }}
            placeholder="Français (Natif), Anglais (C1)…"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <button
            type="button"
            onClick={handleAddLang}
            disabled={!newLang.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white/80 transition-all disabled:opacity-40 text-sm shrink-0"
          >
            <Plus className="size-4" />
          </button>
        </div>
      </div>

      {/* Hobbies */}
      <div className="space-y-3">
        <Label className="text-sm text-white/80">Centres d'intérêt</Label>
        <div className="flex flex-wrap gap-2">
          {data.hobbies.map((hobby, i) => (
            <span
              key={i}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 text-sm text-white/70"
            >
              {hobby}
              <button
                type="button"
                onClick={() => removeHobby(i)}
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            value={newHobby}
            onChange={(e) => setNewHobby(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddHobby(); } }}
            placeholder="Photographie, Escalade, Open source…"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <button
            type="button"
            onClick={handleAddHobby}
            disabled={!newHobby.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white/80 transition-all disabled:opacity-40 text-sm shrink-0"
          >
            <Plus className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
